import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import { ChevronDownSolid, ChevronUpSolid } from '../../utils/icons';
import { Button, Dropdown, MenuProps } from 'antd';
import useStyle from './index.styles';
import { MouseEventHandler, useEffect, useState } from 'react';
import { ReactComponent as Logo } from '../../assets/images/logo.svg';
import { ReactComponent as LogoMobile } from '../../assets/images/logoMobile.svg';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useIsMobile } from '../../utils/hooks';
import { BarsSolid, ChevronRightSolid, CircleArrowLeftSolid } from '../../utils/icons';
import DrawerChangeLanguageMobile from './DrawerChangeLanguageMobile';
import Login from '../Login';
import { useAnimalHeaven } from '../../context/animalHeaven.provider';
import { logout } from '../../context/animalHeaven.actions';
import { logout as logoutToApi } from '../../utils/actions';
import useApi from '../../utils/api';
import {
  getNamePathToPath,
  gotToBlogInWordPress,
  gotToHomeInWordPress,
  gotToUsInWordPress,
} from '../../utils';

const AppBar = () => {
  const [searchParams] = useSearchParams();
  const [secondBarOptionOpen, setSecondBarOptionOpen] = useState<null | number>(null);
  const [isOpenMenu, setIsOpenMenu] = useState<boolean>(false);
  const [secondaryOptionsToShow, setSecondaryOptionsToShow] = useState<
    '' | 'adoption' | 'myHistory'
  >('');
  const [showDrawerChangeLanguage, setShowDrawerChangeLanguage] = useState<boolean>(false);
  const [showLogin, setShowLogin] = useState<boolean>(searchParams.has('showLogin'));
  const [isAddOptionsOpen, setIsAddOptionsOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const { api } = useApi();
  const {
    animalHeavenDispatch,
    token,
    user: { hasAdoptionCenter, isAdmin },
  } = useAnimalHeaven();
  const classes = useStyle();
  const currentPage = useLocation().pathname;
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isLogged = !!token;
  const location = useLocation();

  useEffect(() => {
    if (!isOpenMenu && secondaryOptionsToShow) {
      setSecondaryOptionsToShow('');
    }
  }, [isOpenMenu, secondaryOptionsToShow]);

  useEffect(() => {
    if (searchParams.has('showLogin')) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('showLogin');
      navigate({ pathname: location.pathname, search: searchParams.toString() }, { replace: true });
    }
  }, [location.pathname, location.search, navigate, searchParams]);

  const firstBarOptions: { className: string; title: string; onClick?: MouseEventHandler }[] = [
    ...(isLogged
      ? [
          {
            className: 'smallParagraph mr-[8px] p-[8px] cursor-pointer',
            onClick: () => {
              api(logoutToApi({ token: localStorage.getItem('animal_heaven_token') || '' }));
              animalHeavenDispatch(logout());
              navigate(getNamePathToPath('animals-in-heaven'));
            },
            title: t('appBar.logout'),
          },
          {
            className: 'smallParagraph mr-[8px] p-[8px] cursor-pointer',
            onClick: () => navigate('/account'),
            title: t('appBar.myAccount'),
          },
        ]
      : [
          {
            className: 'smallParagraph mr-[8px] p-[8px] cursor-pointer',
            onClick: () => setShowLogin(true),
            title: t('appBar.login'),
          },
          {
            className: 'smallParagraph mr-[8px] p-[8px] cursor-pointer',
            onClick: () => navigate('/add-memorial'),
            title: t('common.registerPetMemorial'),
          },
        ]),
    ...(!hasAdoptionCenter
      ? [
          {
            className: 'smallParagraph mr-[8px] p-[8px] cursor-pointer',
            onClick: () => navigate('/my-adoption-center/add'),
            title: t('appBar.registerAdoptionCenter'),
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            className: 'smallParagraph mr-[8px] p-[8px] cursor-pointer',
            onClick: () => navigate(getNamePathToPath('admin')),
            title: t('appBar.admin'),
          },
        ]
      : []),
    {
      className: 'smallParagraph mr-[16px] p-[8px] cursor-pointer',
      onClick: () => navigate('/contact-us'),
      title: t('appBar.contactUs'),
    },
  ];

  const secondBarOptions: {
    dropdownItems: MenuProps['items'];
    onClick?: Function;
    title: string;
    url: string;
  }[] = [
    {
      title: t('appBar.home'),
      url: '',
      dropdownItems: [],
      onClick: gotToHomeInWordPress,
    },
    {
      title: t('appBar.us'),
      url: '',
      dropdownItems: [],
      onClick: gotToUsInWordPress,
    },
    {
      title: t('appBar.mission'),
      url: getNamePathToPath('mission'),
      dropdownItems: [],
    },
    {
      title: t('appBar.blog'),
      url: '',
      dropdownItems: [],
      onClick: gotToBlogInWordPress,
    },
    {
      title: t('appBar.animalsInHeaven'),
      url: getNamePathToPath('animals-in-heaven'),
      dropdownItems: [],
    },
    {
      title: t('appBar.adoption'),
      url: '',
      dropdownItems: [
        {
          label: (
            <div
              className={`${
                currentPage.includes('/adoption-centers')
                  ? classes.mobileOptionSelectedOrDropdownOptionSelected
                  : ''
              }`}
            >
              {t('appBar.adoptionOptions.adoptionCenters')}
            </div>
          ),
          key: 0,
          onClick: () => {
            setSecondBarOptionOpen(null);
            navigate(getNamePathToPath('adoption-centers'));
          },
        },
        {
          label: (
            <div
              className={`${
                currentPage.includes('/animals-for-adoption')
                  ? classes.mobileOptionSelectedOrDropdownOptionSelected
                  : ''
              }`}
            >
              {t('appBar.adoptionOptions.animalsForAdoption')}
            </div>
          ),
          key: 1,
          onClick: () => {
            setSecondBarOptionOpen(null);
            navigate(getNamePathToPath('animals-for-adoption'));
          },
        },
      ],
    },
    ...(isLogged
      ? [
          {
            title: t('appBar.myHistory'),
            url: '',
            dropdownItems: [
              {
                label: (
                  <div
                    className={`${
                      currentPage.includes('/my-memorials')
                        ? classes.mobileOptionSelectedOrDropdownOptionSelected
                        : ''
                    }`}
                  >
                    {t('appBar.myHistoryOptions.myMemorials')}
                  </div>
                ),
                key: 0,
                onClick: () => {
                  setSecondBarOptionOpen(null);
                  navigate(getNamePathToPath('my-memorials'));
                },
              },
              {
                label: (
                  <div
                    className={`${
                      currentPage.includes('/my-animals-for-adoption')
                        ? classes.mobileOptionSelectedOrDropdownOptionSelected
                        : ''
                    }`}
                  >
                    {t('appBar.myHistoryOptions.myAnimalsForAdoption')}
                  </div>
                ),
                key: 1,
                onClick: () => {
                  setSecondBarOptionOpen(null);
                  navigate(getNamePathToPath('my-animals-for-adoption'));
                },
              },
              {
                label: (
                  <div
                    className={`${
                      currentPage === getNamePathToPath('view-my-adoption-center')
                        ? classes.mobileOptionSelectedOrDropdownOptionSelected
                        : ''
                    }`}
                    onClick={() => {
                      setSecondBarOptionOpen(null);
                      navigate(getNamePathToPath('view-my-adoption-center'));
                    }}
                  >
                    {t('appBar.myHistoryOptions.myAdoptionCenter')}
                  </div>
                ),
                key: 2,
              },
            ],
          },
        ]
      : []),
  ];

  const menuItemsMobile: MenuProps['items'] = [
    {
      label: <p>{t('common.menu')}</p>,
      key: 0,
      type: 'group',
    },
    ...(isLogged
      ? [
          {
            label: (
              <p
                onClick={() => {
                  setIsOpenMenu(false);
                  api(logoutToApi({ token: localStorage.getItem('animal_heaven_token') || '' }));
                  animalHeavenDispatch(logout());
                  navigate(getNamePathToPath('animals-in-heaven'));
                }}
              >
                {t('appBar.logout')}
              </p>
            ),
            key: 1,
          },
          {
            label: (
              <p
                onClick={() => {
                  setIsOpenMenu(false);
                  navigate('/account');
                }}
                className={`${
                  currentPage === '/account'
                    ? classes.mobileOptionSelectedOrDropdownOptionSelected
                    : ''
                }`}
              >
                {t('appBar.myAccount')}
              </p>
            ),
            key: 2,
          },
          ...(isAdmin
            ? [
                {
                  label: (
                    <p
                      onClick={() => {
                        setIsOpenMenu(false);
                        navigate(getNamePathToPath('admin'));
                      }}
                    >
                      {t('appBar.admin')}
                    </p>
                  ),
                  key: 'admin',
                },
              ]
            : []),
        ]
      : [
          {
            label: (
              <p
                onClick={() => {
                  setIsOpenMenu(false);
                  setShowLogin(true);
                }}
              >
                {t('appBar.login')}
              </p>
            ),
            key: 1,
          },
          {
            label: (
              <p
                onClick={() => {
                  setIsOpenMenu(false);
                  navigate('/add-memorial');
                }}
                className={`${
                  currentPage === '/add-memorial'
                    ? classes.mobileOptionSelectedOrDropdownOptionSelected
                    : ''
                }`}
              >
                {t('common.registerPetMemorial')}
              </p>
            ),
            key: 2,
          },
        ]),

    {
      label: (
        <p
          onClick={() => {
            setIsOpenMenu(false);
            setShowDrawerChangeLanguage(true);
          }}
        >
          {t('appBar.changeLanguage')}
        </p>
      ),
      key: 3,
    },
    ...(!hasAdoptionCenter
      ? [
          {
            label: (
              <p
                onClick={() => {
                  navigate('/my-adoption-center/add');
                  setIsOpenMenu(false);
                }}
                className={`${
                  currentPage === '/my-adoption-center/add'
                    ? classes.mobileOptionSelectedOrDropdownOptionSelected
                    : ''
                }`}
              >
                {t('appBar.registerAdoptionCenter')}
              </p>
            ),
            key: 4,
          },
        ]
      : []),
    {
      label: <div className="min-h-[1px] bg-bg1"></div>,
      key: 5,
    },
    {
      label: (
        <p
          onClick={() => {
            gotToHomeInWordPress();
            setIsOpenMenu(false);
          }}
        >
          {t('appBar.home')}
        </p>
      ),
      key: 6,
    },
    {
      label: (
        <p
          onClick={() => {
            gotToUsInWordPress();
            setIsOpenMenu(false);
          }}
        >
          {t('appBar.us')}
        </p>
      ),
      key: 7,
    },
    {
      label: (
        <p
          onClick={() => {
            navigate(getNamePathToPath('mission'));
            setIsOpenMenu(false);
          }}
          className={`${
            currentPage.includes('/mission')
              ? classes.mobileOptionSelectedOrDropdownOptionSelected
              : ''
          }`}
        >
          {t('appBar.mission')}
        </p>
      ),
      key: 8,
    },
    {
      label: (
        <p
          onClick={() => {
            setIsOpenMenu(false);
            gotToBlogInWordPress();
          }}
        >
          {t('appBar.blog')}
        </p>
      ),
      key: 9,
    },
    {
      label: (
        <p
          onClick={() => {
            navigate(getNamePathToPath('animals-in-heaven'));
            setIsOpenMenu(false);
          }}
          className={`${
            currentPage.includes('/animals-in-heaven')
              ? classes.mobileOptionSelectedOrDropdownOptionSelected
              : ''
          }`}
        >
          {t('appBar.animalsInHeaven')}
        </p>
      ),
      key: 10,
    },
    {
      label: (
        <p
          className={`flex w-full items-center justify-between ${
            currentPage.includes('/animals-for-adoption') ||
            currentPage.includes('/adoption-centers')
              ? classes.mobileOptionSelectedOrDropdownOptionSelected
              : ''
          }`}
          onClick={(e) => {
            e.stopPropagation();
            setSecondaryOptionsToShow('adoption');
          }}
        >
          {t('appBar.adoption')}
          <ChevronRightSolid className="mr-[8px] [&>path]:fill-text2" />
        </p>
      ),
      key: 11,
    },
    ...(isLogged
      ? [
          {
            label: (
              <p
                onClick={(e) => {
                  e.stopPropagation();
                  setSecondaryOptionsToShow('myHistory');
                }}
                className={`flex w-full items-center justify-between ${
                  currentPage.includes('/my-memorials') ||
                  currentPage === getNamePathToPath('view-my-adoption-center') ||
                  currentPage.includes('/my-animals-for-adoption')
                    ? classes.mobileOptionSelectedOrDropdownOptionSelected
                    : ''
                }`}
              >
                {t('appBar.myHistory')}
                <ChevronRightSolid className="mr-[8px] [&>path]:fill-text2" />
              </p>
            ),
            key: 12,
          },
        ]
      : []),
  ];

  const adoptionOptions: MenuProps['items'] = [
    {
      label: <p>{t('appBar.adoption')}</p>,
      key: 0,
      type: 'group',
    },
    {
      key: 1,
      label: (
        <p
          onClick={() => {
            setIsOpenMenu(false);
            navigate(getNamePathToPath('adoption-centers'));
          }}
          className={`${
            currentPage.includes('/adoption-centers')
              ? classes.mobileOptionSelectedOrDropdownOptionSelected
              : ''
          }`}
        >
          {t('appBar.adoptionOptions.adoptionCenters')}
        </p>
      ),
    },
    {
      label: (
        <p
          onClick={() => {
            setIsOpenMenu(false);
            navigate(getNamePathToPath('animals-for-adoption'));
          }}
          className={`${
            currentPage === '/animals-for-adoption'
              ? classes.mobileOptionSelectedOrDropdownOptionSelected
              : ''
          }`}
        >
          {t('appBar.adoptionOptions.animalsForAdoption')}
        </p>
      ),
      key: 2,
    },
  ];

  const myHistoryOptions: MenuProps['items'] = [
    {
      label: <p>{t('appBar.myHistory')}</p>,
      key: 0,
      type: 'group',
    },
    {
      label: (
        <p
          className={`${
            currentPage.includes('/my-memorials')
              ? classes.mobileOptionSelectedOrDropdownOptionSelected
              : ''
          }`}
        >
          {t('appBar.myHistoryOptions.myMemorials')}
        </p>
      ),
      key: 1,
      onClick: () => {
        setIsOpenMenu(false);
        navigate(getNamePathToPath('my-memorials'));
      },
    },
    {
      label: (
        <p
          className={`${
            currentPage.includes('/my-animals-for-adoption')
              ? classes.mobileOptionSelectedOrDropdownOptionSelected
              : ''
          }`}
        >
          {t('appBar.myHistoryOptions.myAnimalsForAdoption')}
        </p>
      ),
      key: 2,
      onClick: () => {
        setIsOpenMenu(false);
        navigate(getNamePathToPath('my-animals-for-adoption'));
      },
    },
    {
      label: (
        <p
          onClick={() => {
            setIsOpenMenu(false);
            navigate(getNamePathToPath('view-my-adoption-center'));
          }}
          className={`${
            currentPage === getNamePathToPath('view-my-adoption-center')
              ? classes.mobileOptionSelectedOrDropdownOptionSelected
              : ''
          }`}
        >
          {t('appBar.myHistoryOptions.myAdoptionCenter')}
        </p>
      ),
      key: 3,
    },
  ];

  const isSecondBarOptionSelected = (title: string, url: string) =>
    currentPage === url ||
    (title === t('appBar.mission') && currentPage.includes('/mission')) ||
    (url.includes('/animals-in-heaven') && currentPage.includes('/animals-in-heaven')) ||
    (title === t('appBar.myHistory') &&
      (currentPage.includes('/my-memorials') ||
        currentPage === getNamePathToPath('view-my-adoption-center') ||
        currentPage.includes('/my-animals-for-adoption'))) ||
    (title === t('appBar.adoption') &&
      (currentPage.includes('/animals-for-adoption') || currentPage.includes('/adoption-centers')));

  const isDisabledAddButton = Boolean(
    (currentPage.includes('/add-memorial') && currentPage.split('/').length > 2) ||
      (currentPage.includes('/animal-adoption-form') && currentPage.split('/').length > 2)
  );

  return (
    <>
      <DrawerChangeLanguageMobile
        onClose={() => setShowDrawerChangeLanguage(false)}
        open={showDrawerChangeLanguage}
      />
      {showLogin && <Login onClose={() => setShowLogin(false)} />}
      {!isMobile && (
        <div className="relative">
          <Logo
            className="absolute left-[40px] top-[16px] cursor-pointer bg-bg4"
            onClick={gotToHomeInWordPress}
          />
          <div className="flex min-h-[41px] justify-end px-[16px] shadow-down">
            <div className="flex items-center">
              {firstBarOptions.map(({ className, title, onClick }, i) => (
                <p className={className} onClick={onClick} key={`first-bar-option-${i}`}>
                  {title}
                </p>
              ))}
              <p className="smallParagraph my-[8px] min-h-[16px] cursor-pointer border-0 border-r-[2px] border-solid border-text1"></p>
            </div>
            <LanguageSelector />
          </div>
          <div className="flex items-center justify-end px-[16px] py-[20px] shadow-down">
            <>
              {secondBarOptions.map((e, i) => (
                <Dropdown
                  menu={{ items: e.dropdownItems }}
                  trigger={['click']}
                  placement="bottomRight"
                  overlayClassName="dropdown-menu"
                  onOpenChange={(open) => setSecondBarOptionOpen(open ? i : null)}
                  disabled={e.dropdownItems?.length === 0}
                  key={`secondBarOption-${i}`}
                >
                  <div
                    className={`${classes.secondBarOption} ${
                      isSecondBarOptionSelected(e.title, e.url)
                        ? classes.secondBarOptionSelected
                        : ''
                    }`}
                  >
                    <p
                      onClick={() => {
                        e.url && navigate(e.url);
                        e.onClick && e.onClick();
                      }}
                    >
                      {e.title}
                    </p>
                    {e.dropdownItems?.length !== 0 &&
                      (secondBarOptionOpen === i ? <ChevronUpSolid /> : <ChevronDownSolid />)}
                  </div>
                </Dropdown>
              ))}
              {isLogged && (
                <>
                  {!hasAdoptionCenter && (
                    <Button
                      className="secondary"
                      onClick={() => navigate('/add-memorial')}
                      disabled={isDisabledAddButton}
                    >
                      {t('appBar.addPetMemorial')}
                    </Button>
                  )}
                  {hasAdoptionCenter && (
                    <Dropdown
                      disabled={isDisabledAddButton}
                      menu={{
                        items: [
                          {
                            label: <p>{t('appBar.petMemorial')}</p>,
                            key: 1,
                            onClick: () => {
                              setIsAddOptionsOpen(false);
                              navigate('/add-memorial');
                            },
                          },
                          {
                            label: <p>{t('appBar.animalForAdoption')}</p>,
                            key: 2,
                            onClick: () => {
                              setIsAddOptionsOpen(false);
                              navigate('/animal-adoption-form');
                            },
                          },
                        ],
                      }}
                      trigger={['click']}
                      placement="bottomRight"
                      overlayClassName="dropdown-menu"
                      overlayStyle={{ minWidth: '124px' }}
                      onOpenChange={(open) => setIsAddOptionsOpen(open)}
                    >
                      <Button className="secondary" disabled>
                        {t('appBar.add')}
                        {isAddOptionsOpen ? (
                          <ChevronUpSolid className="ml-[6px] [&>path]:fill-primary60" />
                        ) : (
                          <ChevronDownSolid className="ml-[6px] [&>path]:fill-primary60" />
                        )}
                      </Button>
                    </Dropdown>
                  )}
                </>
              )}
            </>
          </div>
        </div>
      )}
      {isMobile && (
        <div className="flex min-h-[76px] items-center">
          <Dropdown
            menu={{
              items:
                secondaryOptionsToShow === 'adoption'
                  ? adoptionOptions
                  : secondaryOptionsToShow === 'myHistory'
                    ? myHistoryOptions
                    : menuItemsMobile,
            }}
            placement="bottomRight"
            overlayClassName={`dropdown-menu ${isMobile ? 'menu-mobile' : ''}`}
            overlayStyle={{ minWidth: '240px' }}
            open={isOpenMenu}
          >
            <div className="flex w-full items-center justify-between">
              <div className="flex w-[40px] justify-end">
                {isOpenMenu ? (
                  <div
                    className="[&>svg>path]:fill-primary60"
                    onClick={() => {
                      if (secondaryOptionsToShow) {
                        setSecondaryOptionsToShow('');
                      } else {
                        setIsOpenMenu(false);
                      }
                    }}
                  >
                    <CircleArrowLeftSolid />
                  </div>
                ) : (
                  <BarsSolid
                    className="[&>path]:fill-primary90"
                    onClick={() => setIsOpenMenu(true)}
                  />
                )}
              </div>
              <LogoMobile
                className="flex-1"
                onClick={() => {
                  gotToHomeInWordPress();
                  isOpenMenu && setIsOpenMenu(false);
                }}
              />
              <div className="w-[40px]"></div>
            </div>
          </Dropdown>
        </div>
      )}
    </>
  );
};

export default AppBar;
